/**
 * Blog enhancements — table of contents and list load-more.
 *
 * Both are progressive enhancements: without JS the article still reads and
 * the list still paginates through the normal <a href> links.
 */

/**
 * Builds a table of contents from the <h2> elements of an article body,
 * giving each one a stable slug id, and highlights the section in view.
 *
 * <article-toc data-body="#ArticleBody">
 *   <nav data-toc-rail><ol data-toc-list></ol></nav>
 * </article-toc>
 */
class ArticleToc extends HTMLElement {
  #observer = null;
  #onOffset = null;

  connectedCallback() {
    const body = document.querySelector(this.dataset.body || '');
    if (!body) return;

    /* The word-count gate lives in Liquid — this element is only rendered for
       articles that pass it. Here we only bail when there is nothing to list. */
    const headings = [...body.querySelectorAll('h2')];
    if (headings.length < 2) return;

    const lists = this.querySelectorAll('[data-toc-list]');
    if (!lists.length) return;

    const used = new Set();
    const entries = [];

    for (const heading of headings) {
      const id = heading.id || ArticleToc.#uniqueSlug(heading.textContent, used);
      used.add(id);
      heading.id = id;
      entries.push({ id, text: heading.textContent.trim(), heading });
    }

    for (const list of lists) {
      const fragment = document.createDocumentFragment();
      for (const entry of entries) {
        const item = document.createElement('li');
        item.className = 'article-toc__item';

        const link = document.createElement('a');
        link.className = 'article-toc__link';
        link.href = `#${entry.id}`;
        link.textContent = entry.text;
        link.dataset.tocFor = entry.id;

        item.append(link);
        fragment.append(item);
      }
      list.replaceChildren(fragment);
    }

    this.hidden = false;
    this.#spy(entries);

    /* The header height is measured after layout and changes on resize. */
    this.#onOffset = () => this.#spy(entries);
    document.addEventListener('blog:anchor-offset', this.#onOffset);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener('blog:anchor-offset', this.#onOffset);
  }

  /** Marks the heading nearest the top of the viewport as current. */
  #spy(entries) {
    if (!('IntersectionObserver' in window)) return;
    this.#observer?.disconnect();

    const offset = parseInt(
      getComputedStyle(document.documentElement).scrollPaddingBlockStart,
      10
    );
    const top = Number.isFinite(offset) ? offset : 96;

    this.#observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          if (!record.isIntersecting) continue;
          this.#setCurrent(record.target.id);
        }
      },
      { rootMargin: `-${top}px 0px -70% 0px`, threshold: 0 }
    );

    for (const entry of entries) this.#observer.observe(entry.heading);
  }

  #setCurrent(id) {
    for (const link of this.querySelectorAll('[data-toc-for]')) {
      link.setAttribute('aria-current', String(link.dataset.tocFor === id));
    }
  }

  static #uniqueSlug(text, used) {
    const base =
      text
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\u0600-\u06ff]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'section';

    if (!used.has(base)) return base;

    let n = 2;
    while (used.has(`${base}-${n}`)) n += 1;
    return `${base}-${n}`;
  }
}

/**
 * Replaces numbered pagination with a "load more" button that appends the next
 * page of cards in place, keeping a "Showing X of N" counter honest.
 *
 * <blog-load-more data-next="/blogs/x?page=2" data-total="85" data-grid="#BlogGrid">
 *   <button data-loadmore-button>…</button>
 * </blog-load-more>
 */
class BlogLoadMore extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.button = this.querySelector('[data-loadmore-button]');
    this.counter = this.querySelector('[data-loadmore-count]');
    this.grid = document.querySelector(this.dataset.grid || '');
    if (!this.button || !this.grid) return;

    this.next = this.dataset.next || '';
    this.total = Number(this.dataset.total || 0);

    this.button.addEventListener('click', this.#load.bind(this));
    this.#updateCount();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #load(event) {
    event.preventDefault();
    if (!this.next) return;

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const response = await fetch(this.next, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, 'text/html');

      const incoming = doc.querySelectorAll('[data-blog-card]');
      if (incoming.length) {
        const firstNew = incoming[0];
        this.grid.append(...incoming);
        /* Move focus to the first new card so keyboard users are not dropped
           back at the top of the list. */
        firstNew.querySelector('a')?.setAttribute('tabindex', '-1');
        firstNew.querySelector('a')?.focus({ preventScroll: true });
      }

      const nextLink = doc.querySelector('[data-loadmore-root]')?.dataset.next || '';
      this.next = nextLink;
      this.dataset.next = nextLink;

      this.#updateCount();
      if (!nextLink) this.hidden = true;
    } catch (error) {
      if (error.name === 'AbortError') return;
      /* Fall back to the real pagination link rather than stranding the user. */
      window.location.assign(this.next);
    } finally {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    }
  }

  #updateCount() {
    if (!this.counter) return;
    const shown = this.grid.querySelectorAll('[data-blog-card]').length;
    const template = this.counter.dataset.template || 'Showing [shown] of [total]';
    this.counter.textContent = template
      .replace('[shown]', String(shown))
      .replace('[total]', String(this.total));
  }
}

/**
 * Makes RTE tables keyboard-scrollable.
 *
 * theme.rteInit() wraps every table in .table-wrapper but gives it no tabindex,
 * so the overflow container is unreachable by keyboard — a keyboard user simply
 * cannot see the right-hand columns of a measurement table on a phone.
 * Also only make it focusable when it actually overflows, so we don't add a
 * pointless tab stop on wide screens.
 */
function enhanceArticleTables() {
  const bodies = document.querySelectorAll('.article-body');
  if (!bodies.length) return;

  for (const body of bodies) {
    for (const wrapper of body.querySelectorAll('.table-wrapper')) {
      const sync = () => {
        const overflows = wrapper.scrollWidth > wrapper.clientWidth + 1;
        if (overflows) {
          wrapper.setAttribute('tabindex', '0');
          wrapper.setAttribute('role', 'region');
          if (!wrapper.hasAttribute('aria-label')) {
            const caption = wrapper.querySelector('caption')?.textContent?.trim();
            wrapper.setAttribute('aria-label', caption || 'Table, scrollable');
          }
        } else {
          wrapper.removeAttribute('tabindex');
          wrapper.removeAttribute('role');
        }
      };

      sync();
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(wrapper);
    }
  }
}

/**
 * Keeps --blog-anchor-offset equal to the sticky site header's height.
 *
 * theme.js pins #SiteHeader with position: fixed once the page scrolls past
 * 250px. With the logo row and the menu row it is about 180px tall on desktop,
 * so a fixed 96px offset left the TOC rail and every jump-link target under
 * the header. The height depends on the viewport, so measure it.
 */
function syncHeaderOffset() {
  const header = document.getElementById('SiteHeader');
  if (!header || header.dataset.sticky !== 'true') return;

  let current = 0;
  const update = () => {
    const next = Math.ceil(header.getBoundingClientRect().height) + 16;
    if (next === current) return;
    current = next;
    document.documentElement.style.setProperty('--blog-anchor-offset', `${next}px`);
    document.dispatchEvent(new CustomEvent('blog:anchor-offset'));
  };

  update();
  if ('ResizeObserver' in window) {
    new ResizeObserver(update).observe(header, { box: 'border-box' });
  }
}

syncHeaderOffset();

/* theme.rteInit() runs on DOMContentLoaded, so queue behind it. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () =>
    requestAnimationFrame(enhanceArticleTables)
  );
} else {
  requestAnimationFrame(enhanceArticleTables);
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleToc);
}
if (!customElements.get('blog-load-more')) {
  customElements.define('blog-load-more', BlogLoadMore);
}
